/**
 * Records API
 *
 * Handles all record-related API calls.
 * Uses Zod validation for critical endpoints to catch type mismatches at runtime.
 *
 * SSOT Pipeline: COMPLETE
 * =======================
 * Types AND validation schemas from @aica/api-types (auto-generated from OpenAPI)
 * Backend Zod → OpenAPI → Generated Types + Schemas
 */

import { BaseApiClient, type AuthenticatedFetch, type ExpectedRefusal } from '../base';
import { API_ENDPOINTS } from '../endpoints';
import { ApiError } from '@/lib/fetch-utils';
import { fetchRawWithAccessFence } from '@/lib/access-revision';
import { PROCESSING_PAUSED_REFUSALS } from '../processing-paused';
// Schemas from @aica/api-types (SSOT) - now compatible with zod/v4
import {
  listRecordsResponseSchema,
  recordCriteriaResponseSchema,
  recordsFilterOptionsResponseSchema,
  recordsCriteriaFilterOptionsResponseSchema,
  recordWithDetailsResponseSchema,
  semanticSearchResponseSchema,
  UNKNOWN_SCRIPT_STEP_ERROR_CODE,
  type SemanticSearchFilterName,
} from '@aica/api-types/schemas';
import type { z } from 'zod/v4';
// Additional types not yet in OpenAPI (transcript, cost estimation, etc.)
import type {
  TranscriptResponse,
  CostEstimationResponse,
  UploadRecordResponse,
  SemanticSearchResponse,
  SuccessResponse,
} from '../types';
import {
  ACCEPTED_PRICE_HEADER,
  formatAcceptedPriceHeader,
  processBacklogResponseSchema,
  startRecordsResponseSchema,
  startQuoteResponseSchema,
  retryProcessingResponseSchema,
} from '@aica/api-types';
import type {
  AcceptedPrice,
  BacklogSummary,
  ProcessBacklogResponse,
  StartRecordsResponse,
  StartQuote,
  RetryProcessingResponse,
} from '@aica/api-types';

// Inferred types from Zod schemas (matches runtime validation exactly)
/**
 * 409 START_PRICE_CHANGED (#1396) on `/start` and `/process-backlog`: the price
 * of a manual start grew while the confirmation was open, and the screen shows
 * the new one — an answer, not a failure (#1751).
 */
const START_PRICE_REFUSALS: readonly ExpectedRefusal[] = [
  { status: 409, code: 'START_PRICE_CHANGED' },
];

/**
 * 400 `unknown_script_step` (#879) of the list and of the semantic search: the
 * step of a `scriptStep` filter is no longer in the organization's scripts. The
 * page drops the filter and says so — an answer, not a failure (#1751).
 */
const UNKNOWN_SCRIPT_STEP_REFUSALS: readonly ExpectedRefusal[] = [
  { status: 400, error: UNKNOWN_SCRIPT_STEP_ERROR_CODE },
];

type RecordsListResponse = z.infer<typeof listRecordsResponseSchema>;
type RecordsFilterOptions = z.infer<typeof recordsFilterOptionsResponseSchema>;
type RecordsCriteriaFilterOptions = z.infer<
  typeof recordsCriteriaFilterOptionsResponseSchema
>;
type RecordDetailResponse = z.infer<typeof recordWithDetailsResponseSchema>;
type RecordResponse = RecordDetailResponse;
type RecordCriteriaResponse = z.infer<typeof recordCriteriaResponseSchema>;

export class RecordsApi extends BaseApiClient {
  private getAuthData?: () => Promise<{ token: string; selectedOrgId: string }>;
  private blobFetch?: (path: string) => Promise<Response>;

  constructor(
    authenticatedFetch: AuthenticatedFetch,
    baseUrl?: string,
    getAuthData?: () => Promise<{ token: string; selectedOrgId: string }>,
    blobFetch?: (path: string) => Promise<Response>
  ) {
    super(authenticatedFetch, baseUrl);
    this.getAuthData = getAuthData;
    this.blobFetch = blobFetch;
  }

  /**
   * Get records list with pagination
   * Uses generated Zod schema from @aica/api-types for validation.
   * Type is inferred from schema (Zod output type).
   */
  /**
   * Массив — повторяющийся параметр (`criterion`, #865): каждое значение
   * отдельной парой, как их ждёт схема запроса.
   */
  async getRecords(
    params?: Record<string, string | readonly string[]>
  ): Promise<RecordsListResponse> {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params ?? {})) {
      if (typeof value === 'string') search.append(key, value);
      else for (const item of value) search.append(key, item);
    }
    const query = params ? `?${search.toString()}` : '';
    // Let TypeScript infer from schema - no explicit type parameter
    // This handles the difference between Zod's input/output types correctly
    const result = await this.validatedRequest(
      `${API_ENDPOINTS.RECORDS}${query}`,
      listRecordsResponseSchema,
      { expectedRefusals: UNKNOWN_SCRIPT_STEP_REFUSALS }
    );
    return result as RecordsListResponse;
  }

  /**
   * Значения динамических фильтров — агенты и проекты, посчитанные СЕРВЕРОМ в
   * границе видимости смотрящего.
   *
   * Раньше обе выпадашки наполнялись на клиенте из уже загруженных строк, и их
   * содержимое зависело от глубины прокрутки: руководитель не находила в
   * фильтре двух своих операторов, чьи звонки лежали на 245 записей ниже, — при
   * том что записи были ей доступны и находились поиском.
   *
   * `includeDemo` — единственный параметр: список намеренно НЕ сужается
   * периодом и проектом, иначе человек снова «пропадал» бы из выпадашки при
   * смене дат.
   */
  async getFilterOptions(includeDemo?: 'yes' | 'no'): Promise<RecordsFilterOptions> {
    const query = includeDemo ? `?includeDemo=${includeDemo}` : '';
    const result = await this.validatedRequest(
      `${API_ENDPOINTS.RECORDS_FILTER_OPTIONS}${query}`,
      recordsFilterOptionsResponseSchema
    );
    return result as RecordsFilterOptions;
  }

  /**
   * Критерии для фильтра списка (#865) — отдельным запросом (#1293): сбой
   * чтения приходит ошибкой, а не пустым перечнем, который выглядел бы как
   * «у организации критериев нет». `includeDemo` — как у `getFilterOptions`:
   * демо-критерии (#1502) отвечены только на демо-звонках.
   */
  async getCriteriaFilterOptions(
    includeDemo?: 'yes' | 'no'
  ): Promise<RecordsCriteriaFilterOptions> {
    const query = includeDemo ? `?includeDemo=${includeDemo}` : '';
    return this.validatedRequest(
      `${API_ENDPOINTS.RECORDS_CRITERIA_FILTER_OPTIONS}${query}`,
      recordsCriteriaFilterOptionsResponseSchema
    );
  }

  /**
   * Get single record by ID with full details
   * Uses generated Zod schema from @aica/api-types for validation.
   * Type is inferred from schema (Zod output type).
   */
  async getRecordById(id: string): Promise<RecordResponse> {
    // Let TypeScript infer from schema - no explicit type parameter
    const result = await this.validatedRequest(
      API_ENDPOINTS.RECORDS_BY_ID(id),
      recordWithDetailsResponseSchema
    );
    return result as RecordResponse;
  }

  async archiveRecord(id: string): Promise<SuccessResponse> {
    return this.request<SuccessResponse>(API_ENDPOINTS.RECORDS_ARCHIVE(id), {
      method: 'POST',
    });
  }

  async deleteRecord(id: string): Promise<SuccessResponse> {
    return this.request<SuccessResponse>(API_ENDPOINTS.RECORDS_BY_ID(id), {
      method: 'DELETE',
    });
  }

  async getRecordTranscript(id: string): Promise<TranscriptResponse> {
    return this.request<TranscriptResponse>(API_ENDPOINTS.RECORDS_TRANSCRIPT(id));
  }

  async retryRecordMedia(id: string): Promise<SuccessResponse> {
    return this.request<SuccessResponse>(API_ENDPOINTS.RECORDS_RETRY_MEDIA(id), {
      method: 'POST',
      expectedRefusals: PROCESSING_PAUSED_REFUSALS,
    });
  }

  async retryRecordAnalysis(
    id: string,
    data: { profileId?: string }
  ): Promise<SuccessResponse> {
    return this.request<SuccessResponse>(API_ENDPOINTS.RECORDS_RETRY_ANALYSIS(id), {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async uploadRecord(data: FormData): Promise<UploadRecordResponse> {
    return this.request<UploadRecordResponse>(API_ENDPOINTS.RECORDS_UPLOAD, {
      method: 'POST',
      body: data as unknown as BodyInit,
    });
  }

  async estimateCost(data: {
    projectId: string;
    fileSize: number;
    channel: 'call' | 'chat';
    diarization?: boolean;
  }): Promise<CostEstimationResponse> {
    return this.request<CostEstimationResponse>(API_ENDPOINTS.RECORDS_ESTIMATE_COST, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Накопленное до включения анализа: объём и цена.
   *
   * Цена приходит с сервера и на клиенте не пересчитывается — она обязана
   * совпасть с тем, что реально спишется при запуске.
   */
  async getBacklogSummary(range?: {
    from?: string;
    to?: string;
  }): Promise<BacklogSummary> {
    // Same window the run itself takes: quoting the whole backlog while
    // starting a narrower range is how a confirmation dialog ends up promising
    // 300 conversations for a run that processes 10.
    const query = new URLSearchParams();
    if (range?.from) query.set('from', range.from);
    if (range?.to) query.set('to', range.to);
    // `query.toString()`, not `query.size`: the latter landed in Safari 17 and
    // Node 19, and on anything older it is `undefined` — `undefined > 0` is
    // false, the suffix vanishes, and the card quotes the WHOLE backlog for a
    // narrowed run with nothing to show that it happened. That is the exact
    // defect this method was written to fix.
    const queryString = query.toString();
    const suffix = queryString ? `?${queryString}` : '';
    return this.request<BacklogSummary>(
      `${API_ENDPOINTS.RECORDS_BACKLOG_SUMMARY}${suffix}`
    );
  }

  /**
   * Запуск накопленного за выбранный период (без периода — всё).
   *
   * Ответ проверяется схемой, а не приводится к типу. Разница видна на
   * рассинхроне деплоев: ui-worker и edge-api катятся отдельно, и браузер с
   * новым бандлом может получить от старого воркера `{queued, remaining}` без
   * `failed`. Приведение типа сделало бы `result.failed` равным `undefined`,
   * ветка «ничего не ушло» приняла бы это за отказ, а i18next напечатал бы
   * счётчик отказов с неразрешённой подстановкой. Схема объявлена SSOT
   * (`processBacklogResponseSchema`) — это и есть её работа (#400).
   */
  async processBacklog(
    range: { from?: string; to?: string } | undefined,
    acceptedPrice: AcceptedPrice
  ): Promise<ProcessBacklogResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.RECORDS_PROCESS_BACKLOG,
      processBacklogResponseSchema,
      {
        method: 'POST',
        body: JSON.stringify(range ?? {}),
        // Цена партии, на которую согласились: сервер не запустит, если она
        // выросла с момента показа (#1396). Заголовком, а не в теле — тело то
        // же, что до #1396, и старый сервер его принимает (см.
        // `ACCEPTED_PRICE_HEADER`).
        extraHeaders: {
          [ACCEPTED_PRICE_HEADER]: formatAcceptedPriceHeader(acceptedPrice),
        },
        expectedRefusals: START_PRICE_REFUSALS,
      }
    );
  }

  /**
   * Ручной запуск выбранных записей.
   *
   * Ответ проверяется схемой, а не приводится к типу: ui-worker и edge-api
   * катятся отдельно, и новый бандл может получить от старого воркера ответ без
   * `skipped` — приведение сделало бы его `undefined`, и тост сказал бы
   * «пропущено undefined записей».
   */
  async startRecords(
    recordIds: string[],
    acceptedPrice: AcceptedPrice
  ): Promise<StartRecordsResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.RECORDS_START,
      startRecordsResponseSchema,
      {
        method: 'POST',
        body: JSON.stringify({ recordIds }),
        // Цена, на которую согласились в диалоге: сервер пересчитает её и не
        // запустит набор, если она выросла (#1396). Заголовком: тело `/start`
        // — строгая схема, и новый интерфейс против старого или откаченного
        // edge-api получал бы 400 на каждый запуск (`ACCEPTED_PRICE_HEADER`).
        extraHeaders: {
          [ACCEPTED_PRICE_HEADER]: formatAcceptedPriceHeader(acceptedPrice),
        },
        expectedRefusals: START_PRICE_REFUSALS,
      }
    );
  }

  /** Цена выбранного набора — для диалога подтверждения. */
  async quoteStart(recordIds: string[]): Promise<StartQuote> {
    return this.validatedRequest(
      API_ENDPOINTS.RECORDS_START_QUOTE,
      startQuoteResponseSchema,
      {
        method: 'POST',
        body: JSON.stringify({ recordIds }),
        // A quote only reads the selection's prices — fenced like a GET.
        accessRead: true,
      }
    );
  }

  async reanalyzeRecord(
    id: string,
    data?: {
      promptProfileId?: string;
      mode?: 'analysis_only' | 'from_asr_source';
      publish?: 'shadow' | 'promote';
      transcriptId?: string;
      expectedCurrentAnalysisId?: string | null;
      operationId?: string;
      deliverToCrm?: false;
    }
  ): Promise<SuccessResponse> {
    return this.request<SuccessResponse>(API_ENDPOINTS.RECORDS_REANALYZE(id), {
      method: 'POST',
      // Generated once before authenticatedFetch performs any transport retry,
      // so a new click creates a new run while a lost response does not create
      // a second paid workflow.
      body: JSON.stringify({
        operationId: data?.operationId ?? crypto.randomUUID(),
        ...data,
      }),
      expectedRefusals: PROCESSING_PAUSED_REFUSALS,
    });
  }

  /**
   * Повтор обработки. `status` в ответе — что произошло: прогон запущен
   * (`queued`) или запись ждёт ручного запуска (`awaiting_start`, #1264).
   */
  async retryTranscription(id: string): Promise<RetryProcessingResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.RECORDS_RETRY_TRANSCRIPTION(id),
      retryProcessingResponseSchema,
      { method: 'POST', expectedRefusals: PROCESSING_PAUSED_REFUSALS }
    );
  }

  /**
   * Результаты критериев на записи — группа «Критерии» в панели и раздел PDF.
   *
   * Ответ разбирается схемой, в отличие от соседнего соответствия скрипту:
   * состояние строки решает, что человек прочитает («неприменимо» вместо
   * «не найдено» — другое утверждение), и молча принять чужую форму нельзя.
   *
   * `analysisId` — анализ, который рисует панель (#2582): источники встроенных
   * читаются у него, а не у текущего анализа PG. `null` — у записи анализа ещё
   * нет (сервер встроенные не читает); без аргумента — текущий анализ.
   */
  async getRecordCriteria(
    id: string,
    analysisId?: string | null
  ): Promise<RecordCriteriaResponse> {
    const query =
      analysisId === undefined
        ? ''
        : `?analysisId=${analysisId === null ? 'none' : encodeURIComponent(analysisId)}`;
    const result = await this.validatedRequest(
      `${API_ENDPOINTS.RECORDS_CRITERIA(id)}${query}`,
      recordCriteriaResponseSchema
    );
    return result as RecordCriteriaResponse;
  }

  async getRecordScriptAdherence(
    id: string
  ): Promise<{ success: boolean; adherence: unknown | null }> {
    return this.request<{ success: boolean; adherence: unknown | null }>(
      API_ENDPOINTS.RECORDS_SCRIPT_ADHERENCE(id)
    );
  }

  /**
   * Get record audio as a Blob URL.
   * Uses raw fetch for binary data (not JSON).
   *
   * **IMPORTANT**: Caller MUST call `URL.revokeObjectURL(url)` when done
   * to prevent memory leaks. The returned blob URL holds the entire audio
   * file in memory until revoked.
   */
  async getRecordAudioBlob(id: string): Promise<string> {
    const path = API_ENDPOINTS.RECORDS_AUDIO(id);

    // Both branches must return a raw Response so we can read the binary body as
    // a Blob — authenticatedFetch parses JSON, so it cannot be used here.
    let response: Response;
    if (this.blobFetch) {
      // Embedded: cookie/session auth, rewrites to /api/v1/embedded/* (no Bearer).
      response = await this.blobFetch(path);
    } else if (this.getAuthData) {
      // Standalone: Clerk Bearer token.
      const authData = await this.getAuthData();
      // Fenced like every GET (E1.5 PR 4b): a late old-revision answer throws
      // `StaleAccessRevisionError` before the body is read.
      response = await fetchRawWithAccessFence(
        authData.selectedOrgId,
        `${this.baseUrl}${path}`,
        {
          headers: {
            Authorization: `Bearer ${authData.token}`,
            'X-Selected-Org-ID': authData.selectedOrgId,
          },
        }
      );
    } else {
      throw new Error('No authentication mechanism available for audio blob download');
    }

    if (!response.ok) {
      // ApiError carries the status so callers can tell an expected RBAC/
      // not-found outcome (403/404) from a real failure when logging.
      throw new ApiError(
        `Failed to load audio: ${response.statusText}`,
        response.status,
        response.statusText,
        response
      );
    }

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  }

  async semanticSearch(
    query: string,
    topK: number = 10,
    /** The Records page's filters, as the list sends them (#1234). */
    filters?: Partial<Record<SemanticSearchFilterName, string | readonly string[]>>
  ): Promise<SemanticSearchResponse> {
    return this.validatedRequest(API_ENDPOINTS.SEARCH, semanticSearchResponseSchema, {
      method: 'POST',
      body: JSON.stringify({ query, topK, ...(filters ? { filters } : {}) }),
      // POST only because the filters do not fit a query string: it reads, so
      // a late answer of an older access revision is refused like a GET's.
      accessRead: true,
      expectedRefusals: UNKNOWN_SCRIPT_STEP_REFUSALS,
    });
  }

  async updateAgentRating(
    recordId: string,
    rating: number | null,
    feedback?: string | null
  ): Promise<void> {
    await this.request<{ ok: boolean }>(API_ENDPOINTS.RECORDS_AGENT_RATING(recordId), {
      method: 'PATCH',
      body: JSON.stringify({ rating, feedback: feedback ?? null }),
    });
  }
}
